import { alpineData } from "./alpine-data";

export function registerContentPoolStore(Alpine: any) {
  Alpine.store("contentPool", {
    selected: {} as Record<string, boolean>,
    selectMode: false,
    _tagVersion: 0, // bumped to invalidate selectedTags() cache after tag mutations
    viewMode: (localStorage.getItem("cp-view-mode") || "grid") as "grid" | "list",
    activeCollection: null as string | null,
    sidebarOpen: localStorage.getItem("marketing-collections-sidebar") !== "false",
    importing: false,

    toggleSelect(id: string) {
      if (this.selected[id]) {
        delete this.selected[id];
      } else {
        this.selected[id] = true;
      }
      this.selectMode = Object.keys(this.selected).length > 0;
    },

    isSelected(id: string): boolean {
      return !!this.selected[id];
    },

    selectedIds(): string[] {
      return Object.keys(this.selected);
    },

    selectedCount(): number {
      return Object.keys(this.selected).length;
    },

    selectAll(ids: string[]) {
      ids.forEach((id) => {
        this.selected[id] = true;
      });
      this.selectMode = true;
    },

    clearSelection() {
      this.selected = {};
      this.selectMode = false;
    },

    selectedTags(): string[] {
      // Access _tagVersion so Alpine re-evaluates when it changes
      void this._tagVersion;
      const tagSet = new Set<string>();
      for (const id of this.selectedIds()) {
        const el = document.querySelector(`[data-item-id="${id}"]`);
        if (el) {
          const tags = el.getAttribute("data-tags");
          if (tags)
            tags.split(",").forEach((t: string) => {
              if (t.trim()) tagSet.add(t.trim());
            });
        }
      }
      return [...tagSet].sort();
    },

    // Call after tag mutations to update data-tags on DOM elements immediately
    updateTagsOnSelected(addTags: string[], removeTags: string[]) {
      for (const id of this.selectedIds()) {
        const el = document.querySelector(`[data-item-id="${id}"]`);
        if (!el) continue;
        const current = new Set((el.getAttribute("data-tags") || "").split(",").filter(Boolean));
        addTags.forEach((t) => current.add(t));
        removeTags.forEach((t) => current.delete(t));
        el.setAttribute("data-tags", [...current].join(","));
      }
      this._tagVersion++;
    },

    init() {
      // Persist view mode changes (Alpine stores don't have $watch; use Alpine.effect)
      Alpine.effect(() => {
        localStorage.setItem("cp-view-mode", this.viewMode);
      });
      // Persist sidebar state changes
      Alpine.effect(() => {
        localStorage.setItem("marketing-collections-sidebar", String(this.sidebarOpen));
      });
      // Stop the importing spinner when the background job finishes and refresh the grid.
      window.addEventListener("content-pool-import-done", () => {
        this.importing = false;
        // Trigger grid refresh after a short delay to let the polling stop
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("content-pool-updated"));
        }, 100);
      });
    },
  });
}

// Alpine component for the grid container — handles import polling
export function contentPoolGrid() {
  return alpineData({
    importPollInterval: null as ReturnType<typeof setInterval> | null,

    init() {
      // Watch importing state for grid polling
      const store = this.$store.contentPool;
      this.$watch(
        () => store.importing,
        (val: boolean) => {
          if (val) {
            this.startImportPolling();
          } else {
            this.stopImportPolling();
          }
        },
      );
    },

    startImportPolling() {
      if (this.importPollInterval) return;
      this.importPollInterval = setInterval(() => {
        window.dispatchEvent(new CustomEvent("content-pool-import-poll"));
      }, 3000);
    },

    stopImportPolling() {
      if (this.importPollInterval) {
        clearInterval(this.importPollInterval);
        this.importPollInterval = null;
      }
    },

    destroy() {
      this.stopImportPolling();
    },
  });
}
